import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { director } from "../../data/portfolio";
import { HeroPortrait } from "./HeroPortrait";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollY } = useScroll();
  const textY = useTransform(scrollY, [0, 900], [0, 40]);

  return (
    <section
      ref={ref}
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden bg-[#050505]"
    >
      {/* Ambient warm glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 55% 45% at 68% 40%, rgba(200,162,91,0.09), transparent 65%)",
        }}
      />

      {/* Reusable hero portrait — swap image via `activePortraitId` in /src/data/portfolio.js */}
      <HeroPortrait alt={`${director.name} — film director portrait`} />

      {/* Text column */}
      <motion.div
        style={{ y: textY }}
        className="relative z-10 min-h-screen flex flex-col justify-center px-6 sm:px-12 max-w-[1400px] mx-auto"
      >
        <div className="max-w-[560px]">
          <motion.div
            initial={{ opacity: 0, scaleX: 0 }}
            animate={{ opacity: 1, scaleX: 1 }}
            transition={{ duration: 1.2, delay: 0.4, ease: [0.19, 1, 0.22, 1] }}
            className="h-px w-16 mb-8 origin-left"
            style={{ backgroundColor: "var(--accent-amber)" }}
          />

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.55 }}
            className="text-[10px] sm:text-xs uppercase tracking-[0.4em] mb-6 sm:mb-8"
            style={{ color: "var(--accent-amber)" }}
          >
            {director.role}
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.4, delay: 0.7, ease: [0.19, 1, 0.22, 1] }}
            className="font-serif font-light text-white leading-[0.9] tracking-tighter text-[15vw] sm:text-[10vw] lg:text-[7.5vw]"
          >
            Valeriy
            <br />
            <span className="italic opacity-95">Rudenko</span>
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.05 }}
            className="mt-10 sm:mt-12"
          >
            <p className="text-sm sm:text-base text-white/75 leading-relaxed font-sans mb-8">
              {director.location}
            </p>

            <div className="flex items-center gap-3 sm:gap-4">
              <a
                href="#showreel"
                data-testid="hero-showreel-btn"
                className="group inline-flex items-center gap-3 border px-6 sm:px-8 py-3 sm:py-4 text-[11px] uppercase tracking-[0.25em] text-white transition-colors duration-500 hover:text-black"
                style={{ borderColor: "var(--accent-amber)" }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--accent-amber)")}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
              >
                <span
                  className="w-1.5 h-1.5 rounded-full transition-colors duration-500 group-hover:bg-black"
                  style={{ backgroundColor: "var(--accent-amber)" }}
                />
                Watch Showreel
              </a>
              <a
                href="#contact"
                data-testid="hero-contact-btn"
                className="inline-flex items-center border border-white/25 px-6 sm:px-8 py-3 sm:py-4 text-[11px] uppercase tracking-[0.25em] text-white/80 hover:border-white hover:text-white transition-all duration-500"
              >
                Contact
              </a>
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-6 right-6 sm:right-12 z-10 flex items-center gap-3"
      >
        <span
          className="text-[10px] uppercase tracking-[0.3em]"
          style={{ color: "var(--accent-amber)" }}
        >
          Scroll
        </span>
        <motion.div
          animate={{ scaleX: [1, 1.4, 1] }}
          transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
          className="w-8 h-px origin-left"
          style={{ backgroundColor: "var(--accent-amber-soft)" }}
        />
      </motion.div>
    </section>
  );
};
